import React, { memo, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, Platform, type TextStyle } from 'react-native';
import Animated, {
  Easing,
  Extrapolation,
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { SvgIcon, type SvgIconName } from '../../components/common/SvgIcon';
import { Clickable, PRESS_PRESETS } from '../../components/common/Clickable';
import { appTheme, type ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { useRtl } from '../../shared/i18n/useRtl';
import {
  DASH_STUDIO_RADIUS,
  androidFlatSurfaceStyle,
  androidScaleFont,
  androidScaleSize,
} from '../../shared/theme/studio/stackScreenShell';
import { JourneyIconMotionWrap, type JourneyIconMotion } from './JourneyIconMotionWrap';

const ANDROID_TEXT_COMPACT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};
const SELECT_MS = 200;
const CHECK_SPRING = { stiffness: 380, damping: 14, mass: 0.55 };
const CARD_BORDER = StyleSheet.hairlineWidth + 0.5;

export type JourneyOptionEmphasis = 'default' | 'goal';

type Props = {
  colors: ThemeColors;
  title: string;
  subtitle: string;
  icon: SvgIconName;
  selected: boolean;
  onPress: () => void;
  tone?: 'error' | 'success' | 'secondary' | 'primary';
  dimmed?: boolean;
  emphasis?: JourneyOptionEmphasis;
  icon_motion?: JourneyIconMotion;
  testID?: string;
};

/** Selectable journey list row — goal + activity steps. */
export const JourneyOptionCard = memo(function JourneyOptionCard({
  colors,
  title,
  subtitle,
  icon,
  selected,
  onPress,
  tone = 'error',
  dimmed = false,
  emphasis = 'default',
  icon_motion = 'none',
  testID,
}: Props) {
  const rtl = useRtl();
  const is_goal = emphasis === 'goal';
  const styles = useMemo(() => createStyles(colors, is_goal), [colors, is_goal]);

  const selected_sv = useSharedValue(selected ? 1 : 0);
  const dim_sv = useSharedValue(dimmed ? 1 : 0);
  const check_sv = useSharedValue(selected ? 1 : 0);

  const tone_color =
    tone === 'error'
      ? colors.error
      : tone === 'success'
        ? colors.success
        : tone === 'primary'
          ? colors.primary
          : colors.secondary;

  const on_tone = colors.white ?? colors.textInverse ?? '#FFFFFF';
  const surface_color = colors.surface;
  const surface_selected = hexToRgba(tone_color, 0.07) as string;
  const border_idle = hexToRgba(colors.border ?? colors.text, 0.28) as string;
  const border_selected = tone_color;
  const icon_well_idle = hexToRgba(colors.text, 0.06) as string;
  const radio_ring_idle = hexToRgba(colors.text, 0.2) as string;

  const metrics = useMemo(() => {
    const ss = androidScaleSize;
    const well = ss(is_goal ? 56 : 44);
    const check = ss(is_goal ? 28 : 24);
    return {
      icon_size: ss(is_goal ? 26 : 22),
      well,
      well_radius: ss(is_goal ? 16 : 14),
      check,
      check_icon: ss(is_goal ? 14 : 13),
    };
  }, [is_goal]);

  useEffect(() => {
    selected_sv.value = withTiming(selected ? 1 : 0, {
      duration: SELECT_MS,
      easing: Easing.out(Easing.cubic),
    });
    if (selected) {
      check_sv.value = 0;
      check_sv.value = withSpring(1, CHECK_SPRING);
    } else {
      check_sv.value = withTiming(0, { duration: 150, easing: Easing.in(Easing.cubic) });
    }
  }, [selected, selected_sv, check_sv]);

  useEffect(() => {
    dim_sv.value = withTiming(dimmed ? 1 : 0, { duration: SELECT_MS });
  }, [dimmed, dim_sv]);

  const shell_animated_style = useAnimatedStyle(() => ({
    opacity: interpolate(dim_sv.value, [0, 1], [1, 0.68]),
  }));

  const card_animated_style = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(selected_sv.value, [0, 1], [surface_color, surface_selected], 'RGB'),
    borderColor: interpolateColor(selected_sv.value, [0, 1], [border_idle, border_selected], 'RGB'),
  }));

  const icon_well_style = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(selected_sv.value, [0, 1], [icon_well_idle, tone_color], 'RGB'),
    transform: [
      {
        scale: interpolate(selected_sv.value, [0, 1], [1, 1.04], Extrapolation.CLAMP),
      },
    ],
  }));

  const icon_idle_style = useAnimatedStyle(() => ({
    opacity: interpolate(selected_sv.value, [0, 1], [1, 0]),
  }));

  const icon_active_style = useAnimatedStyle(() => ({
    opacity: interpolate(selected_sv.value, [0, 1], [0, 1]),
  }));

  const radio_shell_style = useAnimatedStyle(() => {
    const open = selected_sv.value;
    return {
      width: metrics.check,
      height: metrics.check,
      borderWidth: interpolate(open, [0, 1], [1.5, 0]),
      borderColor: interpolateColor(open, [0, 1], [radio_ring_idle, tone_color], 'RGB'),
      backgroundColor: interpolateColor(open, [0, 1], ['transparent', tone_color], 'RGB'),
      transform: [
        {
          scale: interpolate(open, [0, 0.55, 1], [1, 1.08, 1], Extrapolation.CLAMP),
        },
      ],
    };
  });

  const check_mark_style = useAnimatedStyle(() => {
    const open = check_sv.value;
    return {
      opacity: interpolate(open, [0, 0.15, 1], [0, 0, 1], Extrapolation.CLAMP),
      transform: [
        { scale: interpolate(open, [0, 0.6, 1], [0.4, 1.15, 1], Extrapolation.CLAMP) },
        { rotate: `${interpolate(open, [0, 1], [-18, 0], Extrapolation.CLAMP)}deg` },
      ],
    };
  });

  return (
    <Animated.View style={[styles.card_shell, shell_animated_style]}>
      <Animated.View style={[styles.card_surface, card_animated_style]}>
        <Clickable
          testID={testID}
          onPress={onPress}
          useNativePressable
          haptics
          pressScale={PRESS_PRESETS.listRow.scale}
          style={styles.card_inner}
          contentBorderRadius={DASH_STUDIO_RADIUS}
          accessibilityRole="radio"
          accessibilityState={{ selected }}>
          <View style={styles.row}>
            <Animated.View
              style={[
                styles.icon_well,
                {
                  width: metrics.well,
                  height: metrics.well,
                  borderRadius: metrics.well_radius,
                },
                icon_well_style,
              ]}>
              <JourneyIconMotionWrap motion={icon_motion} active={selected}>
                <View style={[styles.icon_stack, { width: metrics.icon_size, height: metrics.icon_size }]}>
                  <Animated.View style={[styles.icon_layer, icon_idle_style]} pointerEvents="none">
                    <SvgIcon name={icon} size={metrics.icon_size} color={colors.textSecondary} />
                  </Animated.View>
                  <Animated.View style={[styles.icon_layer, icon_active_style]} pointerEvents="none">
                    <SvgIcon name={icon} size={metrics.icon_size} color={on_tone} />
                  </Animated.View>
                </View>
              </JourneyIconMotionWrap>
            </Animated.View>

            <View style={styles.text_col}>
              <Text
                style={[styles.title, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}
                numberOfLines={2}>
                {title}
              </Text>
              <Text
                style={[styles.subtitle, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}
                numberOfLines={2}>
                {subtitle}
              </Text>
            </View>

            <View style={styles.radio_wrap}>
              <Animated.View style={[styles.radio, radio_shell_style]}>
                <Animated.View style={check_mark_style}>
                  <SvgIcon name="check" size={metrics.check_icon} color={on_tone} />
                </Animated.View>
              </Animated.View>
            </View>
          </View>
        </Clickable>
      </Animated.View>
    </Animated.View>
  );
});

/** @deprecated Use `JourneyOptionCard`. */
export const JourneyGoalOptionCard = JourneyOptionCard;

function createStyles(c: ThemeColors, is_goal: boolean) {
  const sf = androidScaleFont;
  const ss = androidScaleSize;
  const pad = is_goal ? appTheme.spacing.md + 2 : appTheme.spacing.md;

  return StyleSheet.create({
    card_shell: {
      width: '100%',
      marginBottom: is_goal ? appTheme.spacing.md : appTheme.spacing.sm + 2,
      borderRadius: DASH_STUDIO_RADIUS,
      ...androidFlatSurfaceStyle(),
    },
    card_surface: {
      width: '100%',
      overflow: 'hidden',
      borderRadius: DASH_STUDIO_RADIUS,
      borderWidth: CARD_BORDER,
      borderStyle: 'solid',
      ...androidFlatSurfaceStyle(),
    },
    card_inner: {
      width: '100%',
      padding: pad,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: is_goal ? appTheme.spacing.md + 2 : appTheme.spacing.md,
      minHeight: ss(is_goal ? 56 : 44),
    },
    icon_well: {
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      overflow: 'hidden',
    },
    icon_stack: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    icon_layer: {
      ...StyleSheet.absoluteFillObject,
      alignItems: 'center',
      justifyContent: 'center',
    },
    text_col: {
      flex: 1,
      minWidth: 0,
      justifyContent: 'center',
      gap: is_goal ? 4 : 3,
    },
    title: {
      fontSize: sf(is_goal ? 17 : 16),
      lineHeight: sf(is_goal ? 22 : 21),
      fontWeight: '700',
      letterSpacing: -0.2,
      color: c.text,
      ...ANDROID_TEXT_COMPACT,
    },
    subtitle: {
      fontSize: sf(is_goal ? 13 : 13),
      lineHeight: sf(is_goal ? 18 : 17),
      fontWeight: '500',
      color: hexToRgba(c.text, 0.52),
      ...ANDROID_TEXT_COMPACT,
    },
    radio_wrap: {
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: ss(is_goal ? 28 : 24),
      height: ss(is_goal ? 28 : 24),
    },
    radio: {
      borderRadius: 999,
      alignItems: 'center',
      justifyContent: 'center',
      borderStyle: 'solid',
    },
  });
}
